import { api } from '../../app/api/baseApi';
import { PAGINATION_PAYLOAD } from '../../constants/paginationQuery';
import { normalizeUpcomingStatus } from '../../utils/upcomingStatus';
import { environmentalStressMonitoringPreviewResponse } from './environmentalStressMonitoringPreview.mock';
import type {
    EnvironmentalFilterResponse,
    EnvironmentalMetricsResponse,
    EnvironmentalSearchResponse,
    EnvironmentalStressMonitoringFilterRequest,
    EnvironmentalStressMonitoringRequest,
} from './environmentalStressMonitoringApi';

/**
 * Arguments accepted by the `updatePredictionStatus` mutation.
 *
 * `status` is deliberately a plain string rather than `UpcomingSelectableStatus`:
 * the event-detail screens (ServiceFans, RenewServiceContract,
 * BatteryDegradationDetected) still pass mixed-case values such as 'Open'.
 */
export type UpdatePredictionStatusArgs = {
    upcomingId: string;
    body: { status: string };
    /** Overrides the redux-selected realm's `x-realm` header (Service Admin browses other realms). */
    realm?: string;
    /** Sent as the `organizationId` header; needed on pages that don't set `selectedOrg`. */
    organizationId?: string;
    /** Skip the global error toast so the caller can surface its own message. */
    suppressErrorToast?: boolean;
};

export type PredictionDownloadArgs = {
    body: {
        page?: number;
        size?: number;
        sort?: { key: string; sortType: string };
        filters: Record<string, unknown>;
    };
    realm?: string;
    organizationId?: string;
};

type GetPredictionByIdArgs =
    | string
    | undefined
    | {
          predictionId: string;
          realm?: string;
          organizationId?: string;
      };

export type PredictionDownloadResponse = {
    data: string;
};

export type ScoreListResponse = {
    grades: Array<{
        grade: string;
        minScore?: number;
        maxScore?: number;
        color?: string;
    }>;
    parameters: Array<{
        label: string;
        value: number;
    }>;
};

export type ScoreListQueryArgs = {
    realmPrefix?: string;
    organizationId?: string;
};

export const predictionApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getEnvironmentalStressMonitoringMetrics: builder.query<
            EnvironmentalMetricsResponse,
            EnvironmentalStressMonitoringRequest
        >({
            query: ({ deviceId, organizationId, window, timeWindow }) => ({
                url: '/predictions/v1/prediction/esm/metrics',
                method: 'GET',
                params: {
                    deviceId,
                    window,
                    ...(timeWindow !== undefined ? { timeWindow } : {}),
                },
                headers: {
                    'Content-Type': 'application/json',
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
            keepUnusedDataFor: 0,
        }),
        getEnvironmentalStressMonitoringFilter: builder.query<
            EnvironmentalFilterResponse,
            EnvironmentalStressMonitoringFilterRequest
        >({
            query: ({ deviceId, organizationId, parameter, parameters, window, page, size, sort, timeWindow }) => {
                const filterParameters =
                    parameters ??
                    (parameter === undefined ? undefined : Array.isArray(parameter) ? parameter : [parameter]);
                return {
                    url: '/predictions/v1/prediction/esm/filter',
                    method: 'POST',
                    body: {
                        deviceId,
                        organizationId,
                        ...(filterParameters ? { parameters: filterParameters } : {}),
                        window,
                        page,
                        size,
                        ...(timeWindow !== undefined ? { timeWindow } : {}),
                        ...(sort
                            ? {
                                  sort: {
                                      ...sort,
                                      sortType: sort.sortType.toUpperCase() === 'ASC' ? 'ASC' : 'DESC',
                                  },
                              }
                            : {}),
                    },
                    headers: {
                        'Content-Type': 'application/json',
                        ...(organizationId ? { organizationId } : {}),
                    },
                };
            },
            keepUnusedDataFor: 0,
        }),
        searchEnvironmentalStressMonitoring: builder.query<
            EnvironmentalSearchResponse,
            EnvironmentalStressMonitoringRequest
        >({
            query: ({ deviceId, organizationId, parameter, window, timeWindow }) => ({
                url: '/predictions/v1/prediction/esm/search',
                method: 'POST',
                body: {
                    deviceId,
                    organizationId,
                    parameter,
                    window,
                    ...(timeWindow !== undefined ? { timeWindow } : {}),
                },
                headers: {
                    'Content-Type': 'application/json',
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
            keepUnusedDataFor: 0,
        }),
        getPredictionFilters: builder.query<any, any>({
            query: (args: any = {}) => {
                const safeArgs = args && typeof args === 'object' ? args : {};
                const { body, realm, organizationId } = safeArgs;
                const bodyClone = body && typeof body === 'object' ? { ...body } : {};
                return {
                    url: '/predictions/v1/prediction/filter',
                    method: 'POST',
                    body: bodyClone,
                    headers: {
                        ...(realm ? { 'x-realm': realm } : {}),
                        ...(organizationId ? { organizationId: organizationId } : {}),
                    },
                };
            },
            providesTags: ['UpcomingList'],
        }),
        getEnvironmentalStressMonitoringPreview: builder.query<any, void>({
            queryFn: () => ({
                data: environmentalStressMonitoringPreviewResponse,
            }),
            keepUnusedDataFor: 0,
        }),
        getEnvironmentalStressMonitoringSummary: builder.query<any, void>({
            queryFn: () => ({
                data: {
                    data: {
                        overview: environmentalStressMonitoringPreviewResponse.data.overview,
                    },
                },
            }),
            keepUnusedDataFor: 0,
        }),
        getMobilePredictions: builder.query<any, any>({
            query: (body) => {
                const payloadClone = JSON.parse(JSON.stringify(body ?? {}));
                const uniqueIdentifiers = Array.isArray(payloadClone?.uniqueIdentifiers)
                    ? payloadClone.uniqueIdentifiers
                    : [];
                return {
                    url: '/predictions/v1/prediction/summary',
                    method: 'POST',
                    body: {
                        uniqueIdentifiers,
                    },
                };
            },
            ...PAGINATION_PAYLOAD,
        }),

        getPredictionById: builder.query<any, GetPredictionByIdArgs>({
            query: (args) => {
                const { predictionId, realm, organizationId } =
                    typeof args === 'string' || args === undefined
                        ? { predictionId: args ?? '', realm: undefined, organizationId: undefined }
                        : args;
                return {
                    url: `/predictions/v1/prediction/${predictionId}`,
                    method: 'GET',
                    headers: {
                        ...(realm ? { 'x-realm': realm } : {}),
                        ...(organizationId ? { organizationId } : {}),
                    },
                };
            },
            providesTags: ['UpcomingById'],
        }),
        getCapacitorRulDetailById: builder.query<any, any>({
            query: (predictionId) => ({
                url: `/predictions/v1/prediction/${predictionId}/capacitor_rul/detail`,
                method: 'GET',
            }),
            providesTags: ['UpcomingById'],
        }),
        getPredictionSummary: builder.query<any, any>({
            query: (payload) => {
                const requestPayload = payload && payload.body ? payload.body : payload;
                const bodyClone = JSON.parse(JSON.stringify(requestPayload ?? {}));

                return {
                    url: '/predictions/v1/prediction/summary',
                    method: 'POST',
                    body: {
                        uniqueIdentifiers: Array.isArray(bodyClone?.uniqueIdentifiers)
                            ? bodyClone.uniqueIdentifiers
                            : [],
                    },
                    headers: {
                        ...(payload?.realm ? { 'x-realm': payload.realm } : {}),
                        ...(payload?.organizationId ? { organizationId: payload.organizationId } : {}),
                    },
                };
            },
        }),
        downloadPrediction: builder.mutation<PredictionDownloadResponse, PredictionDownloadArgs>({
            query: ({ body, realm, organizationId }: PredictionDownloadArgs) => ({
                url: `/predictions/v1/prediction/download`,
                method: 'POST',
                body,
                headers: {
                    ...(realm ? { 'x-realm': realm } : {}),
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
        }),
        updatePredictionAcknowledge: builder.mutation({
            query: ({ upcomingId, body }: any) => ({
                url: `/predictions/v1/prediction/${upcomingId}/acknowledge`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error) => (!error ? ['UpcomingById'] : []),
        }),
        createServiceRequest: builder.mutation({
            query: ({ body, realm }: { body: any; realm?: string }) => ({
                url: '/predictions/v1/user/serviceRequest',
                method: 'POST',
                body,
                headers: {
                    ...(realm ? { 'x-realm': realm } : {}),
                },
            }),
            invalidatesTags: (result, error) => (!error ? ['UpcomingById'] : []),
        }),
        getPredictionEvidence: builder.query<any, any>({
            query: ({ predictionId, type }) => ({
                url: `/predictions/v1/prediction/${predictionId}/${type}/evidence`,
                method: 'GET',
            }),
        }),
        getScoreList: builder.query<ScoreListResponse, void>({
            query: () => ({
                url: `/score/v1/parameters`,
                method: 'GET',
            }),
        }),
        getScoreListWithContext: builder.query<ScoreListResponse, ScoreListQueryArgs>({
            query: ({ realmPrefix, organizationId }) => ({
                url: `/score/v1/parameters`,
                method: 'GET',
                headers: {
                    ...(realmPrefix ? { 'x-realm': realmPrefix } : {}),
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
        }),
        updatePredictionStatus: builder.mutation<unknown, UpdatePredictionStatusArgs>({
            // Callers that surface their own failure message can opt out of the global
            // error toast per call; baseApi reads `suppressErrorToast` from the args.
            query: ({ upcomingId, body, realm, organizationId, suppressErrorToast }: UpdatePredictionStatusArgs) => {
                const headers = {
                    ...(realm ? { 'x-realm': realm } : {}),
                    ...(organizationId ? { organizationId } : {}),
                };
                return {
                    url: `/predictions/v1/prediction/${upcomingId}/status`,
                    method: 'PATCH',
                    body: { ...body, status: normalizeUpcomingStatus(body.status) },
                    ...(Object.keys(headers).length ? { headers } : {}),
                    suppressErrorToast,
                };
            },
            invalidatesTags: (result, error) => (!error ? ['UpcomingById', 'UpcomingList'] : []),
        }),
        resetPredictionLifecycle: builder.mutation({
            query: ({ upcomingId }: any) => ({
                url: `/predictions/v1/prediction/${upcomingId}/lifecycle-reset`,
                method: 'POST',
            }),
            invalidatesTags: (result, error) => (!error ? ['UpcomingById'] : []),
        }),
        updatePredictionInstallationDate: builder.mutation({
            query: ({ upcomingId, body }: any) => ({
                url: `/predictions/v1/prediction/${upcomingId}/installation-date`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error) => (!error ? ['UpcomingById'] : []),
        }),
        getCommentBySerialNumber: builder.query<any, any>({
            query: (serialNumber) => ({
                url: `/predictions/v1/admin/comment/${serialNumber}`,
                method: 'GET',
            }),
            providesTags: ['CommentBySerialNumber'],
        }),
        saveCommentBySerialNumber: builder.mutation({
            query: ({ serialNumber, body }: any) => ({
                url: `/predictions/v1/admin/comment/${serialNumber}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error) => (!error ? ['CommentBySerialNumber'] : []),
        }),
    }),
    overrideExisting: true,
});
export const {
    useGetEnvironmentalStressMonitoringMetricsQuery,
    useGetEnvironmentalStressMonitoringFilterQuery,
    useSearchEnvironmentalStressMonitoringQuery,
    useGetPredictionFiltersQuery,
    useDownloadPredictionMutation,
    useGetEnvironmentalStressMonitoringPreviewQuery,
    useGetEnvironmentalStressMonitoringSummaryQuery,
    useGetMobilePredictionsQuery,
    useGetPredictionByIdQuery,
    useGetCapacitorRulDetailByIdQuery,
    useGetPredictionSummaryQuery,
    useGetPredictionEvidenceQuery,
    useUpdatePredictionAcknowledgeMutation,
    useCreateServiceRequestMutation,
    useGetScoreListQuery,
    useGetScoreListWithContextQuery,
    useUpdatePredictionStatusMutation,
    useResetPredictionLifecycleMutation,
    useUpdatePredictionInstallationDateMutation,
    useGetCommentBySerialNumberQuery,
    useSaveCommentBySerialNumberMutation,
} = predictionApi;
